import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Account, AccountType, TransactionType, MFDvreType } from '../../models/common/trading-common.model';
import {
    MutualfundTradingOrder,
    RedemptionFeeWaiver,
    ShortSettle,
    MutualFundDetail,
    CdscFee,
    CdscFeeRequestWrapper,
    DistributionType,
    PayToType,
    GrossNetIndicator,
    CDSCWaiver
} from '../../models/mutualfund/trading-mutualfund.model';
import { MutualFundTradingServiceStrategy } from './mutualfund-trading-service-strategy.service';

@Injectable()
export class MutualFundTradingService {

    private baseUrl: string;

    fundDetailSubject$: BehaviorSubject<MutualFundDetail> = new BehaviorSubject({} as MutualFundDetail);

    private orderDetailsSubject = new BehaviorSubject(null);
    orderDetailsObservable$ = this.orderDetailsSubject.asObservable();

    fundErrorSubject$: BehaviorSubject<any> = new BehaviorSubject({} as any);
    fundLoadCompleteSubject$: BehaviorSubject<any> = new BehaviorSubject({} as any);
    fundLoadInProgress: boolean = false;

    constructor( @Inject(MutualFundTradingServiceStrategy) private strategies: Array<MutualFundTradingServiceStrategy> ) {}

    private getStrategy(platform: string): MutualFundTradingServiceStrategy {
        return this.strategies.find((strategy) => {
            return strategy.support(platform);
        });
    }

    public getTransactionTypes(platform: string, account: Account): TransactionType[] {
        return this.getStrategy(platform).getTransactionTypes(account);
    }

    public getMFDvreTypes(platform: string, dInd: string, cInd: string): MFDvreType[] {
        return this.getStrategy(platform).getMFDvreTypes(dInd, cInd);
    }

    public getCdscWaiverTypes(platform: string): CDSCWaiver[] {
        return this.getStrategy(platform).getCdscWaiverTypes();
    }

    public getGrossNetTypes(platform: string, isFrontEndLoadFund: boolean): GrossNetIndicator[] {
        return this.getStrategy(platform).getGrossNetTypes(isFrontEndLoadFund);
    }

    public getRedemptionFeeWaiverTypes(platform: string): RedemptionFeeWaiver[] {
        return this.getStrategy(platform).getRedemptionFeeWaiverTypes();
    }

    public getShortSettleTypes(platform: string): ShortSettle[] {
        return this.getStrategy(platform).getShortSettleTypes();
    }

    public getAccountTypes(platform: string, account: Account, transactionType: TransactionType): AccountType[] {
        return this.getStrategy(platform).getAccountTypes(account, transactionType);
    }

    public getDistributionTypes(platform: string): DistributionType[] {
        return this.getStrategy(platform).getDistributionTypes();
    }

    public getPayToTypes(platform: string): PayToType[] {
        return this.getStrategy(platform).getPayToTypes();
    }

    public getMutualFundDetails(platform: string, account: Account, mutualFundID: string): Observable<any> {
        return this.getStrategy(platform).getMutualFundDetails(account, mutualFundID);
    }

    public getPlan529Funds(platform: string, accountNum: string): Observable<any> {
        return this.getStrategy(platform).getPlan529Funds(accountNum);
    }

    public getMutualFundExchangeToList(platform: string, accountNum: string, cusip: string): Observable<any> {
        return this.getStrategy(platform).getMutualFundExchangeToList(accountNum, cusip);
    }

    public reviewMutualFundOrder(platform: string, mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.getStrategy(platform).reviewMutualFundOrder(mutualFundTradingOrder);
    }

    public submitMutualFundOrder(platform: string, mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.getStrategy(platform).submitMutualFundOrder(mutualFundTradingOrder);
    }

    public editMutualFundOrder(platform: string, mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.getStrategy(platform).editMutualFundOrder(mutualFundTradingOrder);
    }

    public sendProspectusRequest(platform: string, reqPayload: any) {
      return this.getStrategy(platform).sendProspectusRequest(reqPayload);
    }

    public getCdscFee(platform: string, cdscFeeRequestWrapper: CdscFeeRequestWrapper): Observable<CdscFee> {
        return this.getStrategy(platform).getCdscFee(cdscFeeRequestWrapper);
    }

    public getLoiRoaDetails(platform: string, acctNum: string, symbol: string, cusip: string, type: string): Observable<any> {
        return this.getStrategy(platform).getLoiRoaDetails(acctNum, symbol, cusip, type);
    }

    public cancelMutualFundOrder(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        return this.getStrategy(platform).cancelMutualFundOrder(acctNum, ordDtlKey, subRequestTypeCode);
    }

    public getOrderDetails(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        return this.getStrategy(platform).getOrderDetails(acctNum, ordDtlKey, subRequestTypeCode);
    }

    public getOrderCommission(platform: string, mutualFundTradingOrder: MutualfundTradingOrder): Observable<any> {
        return this.getStrategy(platform).getOrderCommission(mutualFundTradingOrder);
    }

    public orderDetailsNotification(orderDetails: any) {
        this.orderDetailsSubject.next(orderDetails);
    }
    public fetch529PlanFunds(platform: string, acctNum: string): Observable<any>{
        return  this.getStrategy(platform).getPlan529Funds(acctNum);
     }
}
